import { ApiError, extractErrorMessage } from '../enrollhere-api/api.error';

/** AE-5801: shown instead of the raw 403 body when an owner change is denied. */
export const POLICY_OWNER_SAVE_PERMISSION_MESSAGE =
  "You don't have permission to change policy owners. Ask an admin for policy edit access.";

/** AE-5801: shown when a policy tag save is denied. */
export const POLICY_TAGS_SAVE_PERMISSION_MESSAGE =
  "You don't have permission to change policy tags. Ask an admin for policy edit access.";

/** AE-5801: shown when an added tag left the catalog or the caller's scope. */
export const POLICY_TAGS_UNAVAILABLE_MESSAGE =
  'Some of the tags you added are no longer available. Remove them and save again.';

/** AE-5801: shown when the team tags preview is denied. */
export const POLICY_TEAM_TAGS_PREVIEW_PERMISSION_MESSAGE =
  "You don't have access to check teams on these policies.";
export const SINGLE_POLICY_TEAM_TAGS_PREVIEW_PERMISSION_MESSAGE =
  "You don't have access to check teams on this policy.";

/**
 * Server access-check wording ("Requires policy.all write on ...", "Agent
 * users cannot ...", "Not authorized"). A 403 with a specific business reason,
 * such as a cross-agency owner, keeps its own message.
 */
const ACCESS_CHECK_MESSAGE =
  /policy\.all|^Agents? (users )?cannot|^Not authorized|^Cannot access policy management/i;

/** A 403 / `PermissionError` that means the caller lacks access. */
export function isPermissionApiError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const denied =
    error.status === 403 ||
    error.serverPayload?.['error'] === 'PermissionError';
  if (!denied) {
    return false;
  }
  const message = error.serverMessage;
  return !message || ACCESS_CHECK_MESSAGE.test(message);
}

/**
 * Readable message for a failed owner or participant change: the friendly
 * permission text for a 403, otherwise the server's message (never the raw
 * "Api error 403: Forbidden - {json}" text).
 */
export function policyOwnerSaveErrorMessage(
  error: unknown,
  fallback: string
): string {
  return isPermissionApiError(error)
    ? POLICY_OWNER_SAVE_PERMISSION_MESSAGE
    : extractErrorMessage(error, fallback);
}

/**
 * The warning for a failed team tags preview: the permission text for a 403,
 * otherwise null so the section keeps its generic "couldn't check" copy.
 */
export function teamTagsPreviewErrorMessage(
  error: unknown,
  scope: 'policy' | 'policies' = 'policies'
): string | null {
  if (!isPermissionApiError(error)) {
    return null;
  }
  return scope === 'policy'
    ? SINGLE_POLICY_TEAM_TAGS_PREVIEW_PERMISSION_MESSAGE
    : POLICY_TEAM_TAGS_PREVIEW_PERMISSION_MESSAGE;
}

/**
 * Readable message for a failed `POST /v1/policy/:id/tags`: the permission
 * text for a 403, plain wording for tags that are gone or out of scope (the
 * server names raw tag ids), otherwise the server's message.
 */
export function policyTagsSaveErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (isPermissionApiError(error)) {
    return POLICY_TAGS_SAVE_PERMISSION_MESSAGE;
  }
  if (
    error instanceof ApiError &&
    /^Unknown or inaccessible tags/i.test(error.serverMessage ?? '')
  ) {
    return POLICY_TAGS_UNAVAILABLE_MESSAGE;
  }
  return extractErrorMessage(error, fallback);
}
